import { useCallback, useRef, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  Connection,
  Edge,
  NodeChange,
  EdgeChange,
  useReactFlow,
  Node,
} from 'reactflow';
import 'reactflow/dist/style.css';
import SourceNode from './CustomNodes/SourceNode';
import TransformationNode from './CustomNodes/TransformationNode';
import FilterNode from './CustomNodes/FilterNode';
import AggregationNode from './CustomNodes/AggregationNode';
import JoinNode from './CustomNodes/JoinNode';
import UnionNode from './CustomNodes/UnionNode';
import DestinationNode from './CustomNodes/DestinationNode';
import MultiSplitNode from './CustomNodes/MultiSplitNode';
import TieredDestinationNode from './CustomNodes/TieredDestinationNode';
import { PipelineNodeData } from '../types/pipeline';
import { validateConnection } from '@/lib/connection-manager';
import { getEdgeStyle } from '@/lib/connection-manager';
import type { ValidationResult } from '@/lib/validation-types';
import { toast } from 'sonner';

const nodeTypes = {
  SOURCE: SourceNode,
  TRANSFORMATION: TransformationNode,
  FILTER: FilterNode,
  AGGREGATION: AggregationNode,
  JOIN: JoinNode,
  UNION: UnionNode,
  DESTINATION: DestinationNode,
  // Executive Dashboard Nodes
  MULTI_SPLIT: MultiSplitNode,
  TIERED_DESTINATION: TieredDestinationNode,
  // Legacy support
  source: SourceNode,
  extract: SourceNode,
  transform: TransformationNode,
  transformation: TransformationNode,
  filter: FilterNode,
  aggregation: AggregationNode,
  join: JoinNode,
  union: UnionNode,
  load: DestinationNode,
  destination: DestinationNode,
};

interface PipelineCanvasProps {
  nodes: Node<PipelineNodeData>[];
  edges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  setNodes: React.Dispatch<React.SetStateAction<Node<PipelineNodeData>[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  onSelectNode: (id: string | null) => void;
  validationResult?: ValidationResult;
}

export default function PipelineCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  setNodes,
  setEdges,
  onSelectNode,
  validationResult,
}: PipelineCanvasProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { project } = useReactFlow();

  // Apply validation styling to edges
  const styledEdges = useMemo(() => {
    if (!validationResult) return edges;

    return edges.map(edge => {
      const edgeValidation = validationResult.edgeValidation.get(edge.id);
      if (edgeValidation) {
        const style = getEdgeStyle(edgeValidation.severity);

        // Use icons instead of text labels
        let label: string | undefined;
        if (edgeValidation.severity === 'error') {
          label = '⚠️'; // Red alert for errors
        } else if (edgeValidation.severity === 'warning') {
          label = '⚠'; // Yellow warning for duplicates/unusual flows
        } else if (edgeValidation.isValid) {
          label = undefined; // No icon for valid connections (green is enough)
        }

        return {
          ...edge,
          style,
          label,
          labelBgStyle: { fill: 'transparent' },
          labelStyle: { fontSize: 16, fontWeight: 'bold' },
        };
      }
      return edge;
    });
  }, [edges, validationResult]);

  const onConnect = useCallback(
    (connection: Connection | Edge) => {
      // Validate connection before adding
      const sourceNode = nodes.find(n => n.id === connection.source);
      const targetNode = nodes.find(n => n.id === connection.target);

      if (sourceNode && targetNode) {
        const validation = validateConnection(
          sourceNode as any,
          targetNode as any,
          edges as any[]
        );

        // Permissive: always allow connection but show feedback
        const newEdge = {
          ...connection,
          id: `${connection.source}-${connection.target}`,
        };

        setEdges((eds) => addEdge(newEdge, eds) as Edge[]);

        // Show toast with icon-based feedback
        if (!validation.valid && validation.severity === 'error') {
          toast.error('⚠️ ' + validation.message);
        } else if (!validation.valid && validation.severity === 'warning') {
          toast.warning('⚠ ' + validation.message);
        } else if (validation.valid) {
          toast.success('✓ Connection created');
        }
      } else {
        setEdges((eds) => addEdge(connection, eds) as Edge[]);
      }
    },
    [nodes, edges, setEdges]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      const label = event.dataTransfer.getData('application/reactflow-label');

      if (!type || !wrapperRef.current) {
        return;
      }

      const bounds = wrapperRef.current.getBoundingClientRect();
      const position = project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const id = `${type}-${Date.now()}`;
      setNodes((nds) => [
        ...nds,
        {
          id,
          type,
          position,
          data: {
            label: label || `${type.charAt(0).toUpperCase()}${type.slice(1)}`,
            type: type as any,
          },
        },
      ]);
    },
    [project, setNodes]
  );

  return (
    <div ref={wrapperRef} className="h-[600px] w-full rounded-lg border bg-white">
      <ReactFlow
        nodes={nodes}
        edges={styledEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeClick={(_, node) => onSelectNode(node.id)}
        onPaneClick={() => onSelectNode(null)}
        nodeTypes={nodeTypes}
        fitView
        deleteKeyCode="Delete"
      >
        <Background gap={16} color="#e5e7eb" />
        <MiniMap pannable zoomable nodeStrokeWidth={3} />
        <Controls />
      </ReactFlow>
    </div>
  );
}
